import { useEffect, useState } from 'react'
import { publicationState } from './publicationState'
import { api } from '@/api/client'
import type { Role, Task, TaskDetail } from '@/lib/types'

/** Publication is a separate operation from approval; never imply an unconfirmed merge succeeded. */
export function PublicationStatus({ task, role, onChanged }: {
  task: Task; role: Role; onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const progress = task.publication
  useEffect(() => {
    if (!progress || !['pending', 'retrying'].includes(progress.status)) return
    const timer = window.setInterval(onChanged, 5000)
    return () => window.clearInterval(timer)
  }, [progress?.status, onChanged])
  if (!progress || progress.status === 'published') return null
  const state = publicationState(progress.status)
  async function recover(action: 'retry' | 'reopen') {
    setBusy(true); setError('')
    try {
      await api.post<TaskDetail>(`/tasks/${task.task_id}/publication`, { action, revision: task.revision ?? 0 })
      onChanged()
    } catch (e) { setError((e as Error).message) }
    finally { setBusy(false) }
  }
  return <section className="note" aria-live="polite">
    <h3>{state.label}</h3>
    <p>{state.explanation}</p>
    {progress.error && <p>{progress.error}</p>}
    {progress.status === 'blocked' && <p>Retry after resolving the cause. If the package changed, return to quality check
      and submit the updated package for a new review; retrying does not update the approved revision.</p>}
    {role === 'engineer' && ['blocked', 'confirmation_required'].includes(progress.status) &&
      <div className="row">
        <button disabled={busy} onClick={() => void recover('retry')}>
          {progress.status === 'confirmation_required' ? 'Check publication' : 'Retry publication'}
        </button>
        {progress.status === 'blocked' && <button disabled={busy} onClick={() => void recover('reopen')}>Return to quality check</button>}
      </div>}
    {progress.last_attempt_at && <p className="small muted">
      Last attempt: {new Date(progress.last_attempt_at).toLocaleString()} · {progress.attempts ?? 0} attempts
    </p>}
    {error && <p role="alert">{error}</p>}
  </section>
}
